'use client';

import React, { useState } from 'react';

interface Formation {
  title: string;
  items: string[];
  icon: React.ReactNode;
  accent: string;
  tagColor: string;
}

const formations: Formation[] = [
  {
    title: 'Soft Skills',
    items: [
      'Communication professionnelle et assertivité',
      'Négociation et gestion des conflits',
      'Gestion du stress et intelligence émotionnelle',
      'Leadership et prise de décision',
      'Management d\'équipe et cohésion',
      'Gestion du temps et des priorités',
    ],
    accent: 'border-red-600',
    tagColor: 'bg-red-50 text-red-700',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ),
  },
  {
    title: 'Finance / Comptabilité',
    items: [
      'Analyse financière et tableaux de bord',
      'Contrôle de gestion et reporting',
      'Gestion budgétaire et prévisions',
      'Comptabilité générale et analytique',
      'Fiscalité des entreprises',
      'Gestion de trésorerie',
    ],
    accent: 'border-gold',
    tagColor: 'bg-yellow-50 text-yellow-700',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
  },
  {
    title: 'ISO et Management de la Qualité',
    items: [
      'ISO 9001 – Système de management de la qualité',
      'ISO 45001 – Santé et sécurité au travail',
      'ISO 14001 – Management environnemental',
      'Audit interne qualité',
      'Amélioration continue et Lean Management',
      'Gestion des risques et conformité',
    ],
    accent: 'border-navy-light',
    tagColor: 'bg-blue-50 text-blue-700',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1E3A7A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
  {
    title: 'Social / RH',
    items: [
      'Droit du travail et relations sociales',
      'Gestion des ressources humaines',
      'Recrutement et évaluation des compétences',
      'Gestion de la paie et charges sociales',
      'Gestion des performances et entretiens',
      'Formation des formateurs',
    ],
    accent: 'border-gold',
    tagColor: 'bg-orange-50 text-orange-700',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    title: 'Intelligence Artificielle (AI)',
    items: [
      'Introduction à l\'IA pour les managers',
      'IA générative et outils pratiques (ChatGPT, Copilot…)',
      'Automatisation des processus avec l\'IA',
      'IA appliquée aux RH et au recrutement',
      'IA et analyse de données pour la décision',
      'Éthique et gouvernance de l\'IA en entreprise',
    ],
    accent: 'border-red-600',
    tagColor: 'bg-purple-50 text-purple-700',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
        <path d="M9 9h1M14 9h1M9 13h6"/>
      </svg>
    ),
  },
];

const formats = [
  { label: 'En présentiel', icon: '📍' },
  { label: 'En ligne', icon: '💻' },
  { label: 'Intra-entreprise', icon: '🏢' },
  { label: 'Inter-entreprises', icon: '🤝' },
];

const trainingPhotos = [
  {
    src: '/assets/images/614bc2a3-5d3b-4e07-b12b-8997fb5fa670-1774977729962.JPG',
    alt: 'Salle de conférence élégante avec lustres et disposition en U pour sessions de formation professionnelle',
  },
  {
    src: '/assets/images/PHOTO-2026-03-31-08-07-16-1774977907926.jpeg',
    alt: 'Formateur présentant devant un tableau de conférence à un groupe de professionnels en session de formation',
  },
  {
    src: '/assets/images/PHOTO-2026-03-31-08-05-46-1774978088489.jpg',
    alt: 'Formatrice présentant des tableaux de bord de données sur grand écran à un groupe de professionnels en session de formation',
  },
  {
    src: '/assets/images/tempImage44hVqe-1775075417250.png',
    alt: 'Groupe de professionnels diversifiés debout et discutant dans une salle de formation moderne et lumineuse',
  },
];

const FormationsSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <section id="formations" className="section-light py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="gold-bar mx-auto mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl font-light text-gold mb-4">
            Nos Formations
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Des formations professionnelles organisées par domaine, conçues pour les entreprises et les organisations.
          </p>
        </div>

        {/* Format tags — compact */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {formats.map((format) => (
            <span
              key={format.label}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 shadow-sm"
            >
              <span>{format.icon}</span>
              {format.label}
            </span>
          ))}
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {formations.map((f, i) => (
            <button
              key={f.title}
              onClick={() => setActiveIndex(activeIndex === i ? null : i)}
              className={`text-sm font-medium px-4 py-2 rounded-full border transition-all duration-200 ${
                activeIndex === i
                  ? 'bg-red-700 text-white border-red-700' :'bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-700'
              }`}
            >
              {f.title}
            </button>
          ))}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {formations.map((formation, index) => (
            <div
              key={formation.title}
              className={`bg-white rounded-2xl p-8 border-t-4 ${formation.accent} ic-card shadow-sm transition-all duration-300 hover:shadow-lg ${
                activeIndex !== null && activeIndex !== index ? 'opacity-40' : 'opacity-100'
              }`}
            >
              <div className="flex items-start justify-between mb-5">
                <div>{formation.icon}</div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${formation.tagColor}`}>
                  {index + 1}/{formations.length}
                </span>
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-4">{formation.title}</h3>
              <ul className="space-y-2.5">
                {formation.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0 mt-1.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <a
                  href="#contact"
                  className="text-sm font-semibold text-red-700 hover:text-red-800 flex items-center gap-1.5 transition-colors"
                >
                  Demander un devis
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Training photos */}
        <div className="mt-16">
          <h3 className="text-center text-gray-400 text-sm uppercase tracking-widest font-medium mb-6">Nos sessions de formation</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {trainingPhotos.map((photo) => (
              <div key={photo.src} className="rounded-2xl overflow-hidden h-52 shadow-md">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Entreprises content integrated */}
        <div className="mt-16 bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="gold-bar mb-5" />
              <h3 className="font-serif text-3xl font-light text-gray-800 mb-4 leading-tight">
                Formations sur mesure pour
                <span className="block text-gold">Entreprises</span>
              </h3>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                Nous proposons des <strong className="text-gray-800 font-semibold">formations adaptées aux besoins stratégiques et opérationnels</strong> des entreprises et organisations.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Améliorer la performance des équipes',
                  'Développer les compétences managériales',
                  'Accompagner les transformations organisationnelles',
                  'Renforcer la compétitivité des entreprises',
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                    <span className="text-gray-600 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-red-700 text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-red-800 transition-all duration-300 shadow-sm hover:-translate-y-0.5"
              >
                Demander un devis
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 shadow-md">
              <img
                src="https://images.unsplash.com/photo-1560439514-4e9645039924?w=700&h=500&fit=crop"
                alt="Formateur présentant une session de formation en entreprise à un groupe de professionnels"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm">
            Toutes nos formations sont dispensées par des <strong className="text-gray-700">Formateurs-Experts</strong> certifiés, en intra ou inter-entreprises.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FormationsSection;
